import { makeTimelineObjects } from "@/functions/timelineFunctions";
import { useSelectedContent } from "@/hooks/contextHooks";
import { EditContent } from "@/pages/editor";
import {
	Box,
	Flex,
	IconButton,
	Text,
	Badge,
	HStack,
	useColorModeValue,
	Slider,
	SliderTrack,
	SliderFilledTrack,
	SliderThumb,
	Button,
	Tooltip
} from "@chakra-ui/react";
import { faMagnifyingGlassPlus, faMagnifyingGlassMinus, faLayerGroup, faClock, faCompressArrowsAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactStateType } from "@xnav/types/src/frontend";
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import TimelineCard, { ROW_HEIGHT, ROW_GAP } from "./timelineCard";

export type TimelineCardProps = {
	column: number;
	start: number;
	end: number;
	name: string;
	type: 'polygon' | 'hostpot';
	color: string;
	isSelected?: boolean;
	onClick: () => void;
	onDelete: () => void;
	onDuplicate: () => void;
	onJump: (time: number) => void;
	onDrag: (newStart: number, newEnd: number) => void;
};

const MarkupTimeline: React.FC<{
	time: number;
	videoDuration: number;
	setCurrentTime: (time: number) => void;
}> = ({ time, videoDuration, setCurrentTime }) => {
	const [zoom, setZoom] = useState(100);
	const flooredZoom = useMemo(() => Math.floor(zoom), [zoom]);

	const [wrapperWidth, setWrapperWidth] = useState(0);
	const [wrapperOffset, setWrapperOffset] = useState(0);
	const timelineWidth = useMemo(() => Math.max(100, (wrapperWidth * zoom) / 100), [zoom, wrapperWidth]);
	const [content, setContent] = useSelectedContent() as ReactStateType<EditContent>;

	const [timelineItems, numColumns]: [TimelineCardProps[], number] = useMemo(
		() =>
			makeTimelineObjects(
				content!,
				setContent,
				setCurrentTime
			),
		[content, setContent, setCurrentTime]
	);

	const hasValidClip = Boolean(
		content?.clipStart !== undefined &&
		content?.clipEnd !== undefined &&
		content.clipEnd > content.clipStart
	);
	const effectiveStart = hasValidClip ? content.clipStart! : 0;
	const effectiveEnd = hasValidClip ? content.clipEnd! : videoDuration;
	const effectiveDuration = Math.max(0.1, effectiveEnd - effectiveStart);

	const timeMarkerOffset = useMemo(() => {
		if (effectiveDuration <= 0) return 0;
		const clamped = Math.max(effectiveStart, Math.min(effectiveEnd, time));
		return (timelineWidth * (clamped - effectiveStart)) / effectiveDuration;
	}, [time, effectiveStart, effectiveEnd, effectiveDuration, timelineWidth]);

	const step = useMemo(() => {
		if (effectiveDuration <= 0) return 5;
		const scaledDuration = effectiveDuration / (zoom / 100);
		if (scaledDuration <= 5) return 0.5;
		if (scaledDuration <= 10) return 1;
		if (scaledDuration <= 20) return 2;
		if (scaledDuration <= 40) return 5;
		if (scaledDuration <= 90) return 10;
		if (scaledDuration <= 300) return 15;
		if (scaledDuration <= 600) return 30;
		if (scaledDuration <= 1800) return 60;
		return 120;
	}, [effectiveDuration, zoom]);

	const ticks = useMemo(() => {
		const result = [];
		if (effectiveDuration <= 0) return [];
		for (let t = effectiveStart; t <= effectiveEnd; t += step) {
			result.push(Math.round(t * 10) / 10);
		}
		if (result.length === 0 || Math.abs(result[result.length - 1] - effectiveEnd) > 0.05) {
			result.push(effectiveEnd);
		}
		return result;
	}, [effectiveStart, effectiveEnd, effectiveDuration, step]);

	function formatTime(sec: number): string {
		const mins = Math.floor(sec / 60);
		const secs = Math.floor(sec % 60);
		const tenths = Math.floor((sec % 1) * 10);
		if (step < 1) {
			return `${mins}:${secs.toString().padStart(2, "0")}.${tenths}`;
		}
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	}

	const wrapperRef = useRef<HTMLDivElement>(null);
	const tracksContainerRef = useRef<HTMLDivElement>(null);
	const [isScrubbing, setIsScrubbing] = useState(false);

	useEffect(() => {
		if (!wrapperRef.current) return;
		const observer = new ResizeObserver((entries) => {
			for (let entry of entries) {
				setWrapperWidth(entry.contentRect.width);
				setWrapperOffset(entry.target.getBoundingClientRect().x);
			}
		});
		observer.observe(wrapperRef.current);
		return () => observer.disconnect();
	}, []);

	// Handle seek click/drag on timeline ruler
	const handleSeekFromEvent = useCallback(
		(clientX: number) => {
			if (!tracksContainerRef.current || effectiveDuration <= 0) return;
			const rect = tracksContainerRef.current.getBoundingClientRect();
			const clickX = clientX - rect.left;
			const clampedX = Math.max(0, Math.min(timelineWidth, clickX));
			const newTime = Math.round((effectiveStart + (clampedX * effectiveDuration) / timelineWidth) * 10) / 10;
			setCurrentTime(newTime);
		},
		[timelineWidth, effectiveStart, effectiveDuration, setCurrentTime]
	);

	const handlePlayheadMouseDown = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsScrubbing(true);
	};

	useEffect(() => {
		if (!isScrubbing) return;
		const handleMouseMove = (e: MouseEvent) => {
			handleSeekFromEvent(e.clientX);
		};
		const handleMouseUp = () => {
			setIsScrubbing(false);
		};
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [isScrubbing, handleSeekFromEvent]);

	const rulerBg = useColorModeValue("gray.50", "gray.850");
	const rulerBorderColor = useColorModeValue("gray.200", "gray.750");
	const laneBorderColor = useColorModeValue("gray.100", "gray.800");
	const tickColor = useColorModeValue("gray.400", "gray.600");
	const headerBg = useColorModeValue("white", "gray.900");
	const headerBorderColor = useColorModeValue("gray.200", "gray.750");

	const totalTracksHeight = useMemo(() => {
		return Math.max(76, numColumns * (ROW_HEIGHT + ROW_GAP) + 16);
	}, [numColumns]);

	return (
		<Flex w="100%" h="100%" flexDir="column" bg={useColorModeValue("gray.50", "gray.900")} userSelect="none">
			{/* Timeline Top Controls Toolbar */}
			<Flex
				w="100%"
				alignItems="center"
				justifyContent="space-between"
				px={4}
				py={1.5}
				bg={headerBg}
				borderBottom="1px solid"
				borderColor={headerBorderColor}
				boxShadow="sm"
				zIndex={10}
			>
				{/* Left: Info & Stats */}
				<HStack spacing={3}>
					<HStack spacing={1.5}>
						<FontAwesomeIcon icon={faLayerGroup} style={{ color: "#10b981", fontSize: "13px" }} />
						<Text fontSize="xs" fontWeight="700" letterSpacing="tight" color={useColorModeValue("gray.700", "gray.200")}>
							Timeline
						</Text>
					</HStack>
					<Badge
						colorScheme={timelineItems.length > 0 ? "green" : "gray"}
						variant="subtle"
						borderRadius="full"
						px={2}
						py={0.2}
						fontSize="2xs"
						fontWeight="700"
					>
						{timelineItems.length} {timelineItems.length === 1 ? "Tag" : "Tags"}
					</Badge>
					{hasValidClip ? (
						<HStack spacing={1.5}>
							<Badge colorScheme="teal" variant="solid" borderRadius="full" px={2} py={0.5} fontSize="2xs">
								Trimmed: {effectiveStart.toFixed(1)}s – {effectiveEnd.toFixed(1)}s ({effectiveDuration.toFixed(1)}s)
							</Badge>
							<Text fontSize="2xs" fontWeight="600" opacity={0.75}>
								{(time - effectiveStart).toFixed(1)}s / {effectiveDuration.toFixed(1)}s
							</Text>
							<Button
								size="2xs"
								variant="link"
								colorScheme="red"
								fontSize="2xs"
								onClick={() => setContent({ ...content, clipStart: undefined, clipEnd: undefined })}
							>
								Reset Trim
							</Button>
						</HStack>
					) : (
						<HStack spacing={1} opacity={0.75}>
							<FontAwesomeIcon icon={faClock} style={{ fontSize: "10px" }} />
							<Text fontSize="2xs" fontWeight="600">
								{time.toFixed(1)}s / {videoDuration.toFixed(1)}s
							</Text>
						</HStack>
					)}
				</HStack>

				{/* Right: Zoom Controls */}
				<HStack spacing={3}>
					<HStack spacing={1}>
						<Tooltip label="Reset zoom to 100%" placement="top" hasArrow>
							<Button
								size="xs"
								variant={zoom === 100 ? "solid" : "ghost"}
								colorScheme={zoom === 100 ? "green" : "gray"}
								borderRadius="md"
								fontSize="2xs"
								h="22px"
								px={2}
								onClick={() => setZoom(100)}
							>
								100%
							</Button>
						</Tooltip>
						<Tooltip label="Zoom 200%" placement="top" hasArrow>
							<Button
								size="xs"
								variant={zoom === 200 ? "solid" : "ghost"}
								colorScheme={zoom === 200 ? "green" : "gray"}
								borderRadius="md"
								fontSize="2xs"
								h="22px"
								px={2}
								onClick={() => setZoom(200)}
							>
								200%
							</Button>
						</Tooltip>
					</HStack>

					<HStack spacing={2} w="160px" alignItems="center">
						<IconButton
							aria-label="Zoom out"
							variant="ghost"
							size="xs"
							borderRadius="full"
							icon={<FontAwesomeIcon icon={faMagnifyingGlassMinus} />}
							onClick={() => setZoom((z) => Math.max(100, Math.round(z / 1.2)))}
						/>
						<Slider
							min={100}
							max={1500}
							value={zoom}
							onChange={(val) => setZoom(val)}
							colorScheme="green"
							size="sm"
						>
							<SliderTrack bg={useColorModeValue("gray.200", "gray.700")} h="4px" borderRadius="full">
								<SliderFilledTrack bg="emerald.500" />
							</SliderTrack>
							<SliderThumb boxSize={3.5} bg="white" borderColor="emerald.500" borderWidth="2px" boxShadow="sm" />
						</Slider>
						<IconButton
							aria-label="Zoom in"
							variant="ghost"
							size="xs"
							borderRadius="full"
							icon={<FontAwesomeIcon icon={faMagnifyingGlassPlus} />}
							onClick={() => setZoom((z) => Math.min(1500, Math.round(z * 1.2)))}
						/>
					</HStack>
					<Text fontSize="2xs" fontWeight="700" color="gray.500" minW="36px" textAlign="right">
						{flooredZoom}%
					</Text>
				</HStack>
			</Flex>

			{/* Main Scrollable Timeline Area */}
			<Box
				flex={1}
				w="100%"
				overflowX="auto"
				overflowY="auto"
				ref={wrapperRef}
				pos="relative"
				css={{
					"&::-webkit-scrollbar": { height: "6px", width: "6px" },
					"&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.2)", borderRadius: "9999px" },
				}}
			>
				<Box
					width={`${Math.max(100, zoom)}%`}
					minW={`${timelineWidth}px`}
					pos="relative"
					minH={`${totalTracksHeight + 36}px`}
					ref={tracksContainerRef}
				>
					{/* Time Ruler (Clickable to seek) */}
					<Box
						h="28px"
						bg={rulerBg}
						borderBottom="1px solid"
						borderColor={rulerBorderColor}
						pos="relative"
						w="100%"
						cursor="pointer"
						onMouseDown={(e) => {
							handleSeekFromEvent(e.clientX);
							setIsScrubbing(true);
						}}
					>
						{ticks.map((t) => {
							const left = effectiveDuration > 0 ? (timelineWidth * (t - effectiveStart)) / effectiveDuration : 0;
							return (
								<Box
									key={t}
									pos="absolute"
									left={`${left}px`}
									top={0}
									bottom={0}
									display="flex"
									flexDir="column"
									alignItems="center"
									transform="translateX(-50%)"
									pointerEvents="none"
								>
									<Box w="1px" h="8px" bg={tickColor} mt={0} />
									<Text fontSize="10px" fontWeight="700" color={useColorModeValue("gray.500", "gray.400")} mt="1px">
										{formatTime(t)}
									</Text>
								</Box>
							);
						})}
					</Box>

					{/* Track Lanes Guide Lines */}
					<Box pos="absolute" top="28px" left={0} right={0} bottom={0} pointerEvents="none">
						{Array.from({ length: Math.max(1, numColumns) }).map((_, colIdx) => (
							<Box
								key={colIdx}
								pos="absolute"
								top={`${colIdx * (ROW_HEIGHT + ROW_GAP) + 6}px`}
								left={0}
								right={0}
								height={`${ROW_HEIGHT}px`}
								borderTop="1px dashed"
								borderBottom="1px dashed"
								borderColor={laneBorderColor}
								bg={colIdx % 2 === 0 ? "transparent" : useColorModeValue("blackAlpha.50", "whiteAlpha.50")}
							/>
						))}
					</Box>

					{/* Playhead Scrubber Line & Needle (Draggable) */}
					<Box
						zIndex={120}
						pos="absolute"
						top={0}
						bottom={0}
						left={`${timeMarkerOffset}px`}
						transform="translateX(-50%)"
						w="24px"
						display="flex"
						flexDir="column"
						alignItems="center"
						cursor="ew-resize"
						onMouseDown={handlePlayheadMouseDown}
					>
						{/* Playhead Head (Diamond / Flag with time badge) */}
						<Box
							w="14px"
							h="18px"
							bg="#10b981"
							borderRadius="2px 2px 7px 7px"
							boxShadow="0 2px 8px rgba(16, 185, 129, 0.6)"
							border="2px solid white"
							display="flex"
							alignItems="center"
							justifyContent="center"
							transition="transform 0.1s"
							_hover={{ transform: "scale(1.2)" }}
						/>
						{/* Playhead Vertical Needle */}
						<Box
							flex={1}
							w="2px"
							bg="#10b981"
							boxShadow="0 0 6px rgba(16, 185, 129, 0.8)"
						/>
					</Box>

					{/* Timeline Markup Cards */}
					<Box pos="absolute" top="34px" left={0} right={0} minH={`${totalTracksHeight}px`}>
						{timelineItems.length === 0 ? (
							<Flex
								alignItems="center"
								justifyContent="center"
								h="60px"
								w="100%"
								opacity={0.4}
							>
								<Text fontSize="xs" fontWeight="500">
									No tags or markups created yet
								</Text>
							</Flex>
						) : (
							timelineItems.map((cardProps, index) => (
								<TimelineCard
									key={index}
									{...cardProps}
									videoDuration={effectiveDuration}
									clipStart={effectiveStart}
									numColumns={numColumns}
									timelineWidth={timelineWidth}
									timelineOffset={wrapperOffset}
								/>
							))
						)}
					</Box>
				</Box>
			</Box>
		</Flex>
	);
};

export default MarkupTimeline;
